import { t } from '@/i18n';
import { getStatusContent, StatusIconBadge } from '@/registrations';

type Tone = 'teal' | 'blue';

const toneStyles: Record<Tone, { card: string; inner: string; primary: string }> = {
  // Layer B — same-device memory: filled teal CTA to the token manage page.
  teal: {
    card: 'border-teal-200 bg-teal-50/50 dark:border-teal-900/50 dark:bg-teal-950/30',
    inner: 'border-teal-200 bg-white dark:border-teal-900/50 dark:bg-gray-950',
    primary: 'bg-teal-600 text-white hover:bg-teal-500',
  },
  // Layer C — logged-in: outlined blue CTA to My Signups.
  blue: {
    card: 'border-blue-200 bg-blue-50/40 dark:border-blue-900/50 dark:bg-blue-950/30',
    inner: 'border-blue-200 bg-white dark:border-blue-900/50 dark:bg-gray-950',
    primary: 'border border-blue-300 bg-white text-blue-700 hover:border-blue-400 dark:border-blue-800 dark:bg-transparent dark:text-blue-300',
  },
};

interface RegisteredCardProps {
  status: string;
  paymentStatus: string;
  waitlistPosition?: number | null;
  price?: string | null;
  currency?: string;
  /** Secondary line under the headline (e.g. the registered email). */
  subline?: string;
  tone: Tone;
  primaryHref: string;
  primaryLabel: string;
  /** Optional "Not you? Sign up as someone else" escape (Layer B). */
  onNotYou?: () => void;
}

/**
 * Compact registered state shown on the event page in place of the signup form,
 * for a returning device (Layer B, teal) or a logged-in registrant (Layer C, blue).
 * Reuses the shared status copy/icon so the "did I get in?" answer stays consistent
 * with the manage page and My Signups.
 */
export default function RegisteredCard({
  status,
  paymentStatus,
  waitlistPosition,
  price,
  currency,
  subline,
  tone,
  primaryHref,
  primaryLabel,
  onNotYou,
}: RegisteredCardProps) {
  const content = getStatusContent({
    status,
    payment_status: paymentStatus,
    price: price ?? null,
    currency: currency ?? '',
    waitlist_position: waitlistPosition,
  });
  const styles = toneStyles[tone];

  return (
    <div className={`rounded-2xl border p-6 shadow-sm ${styles.card}`}>
      <div className={`flex items-center gap-3 rounded-xl border p-4 ${styles.inner}`}>
        <StatusIconBadge tone={content.tone} icon={content.icon} waitlistPosition={waitlistPosition} size="sm" />
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 dark:text-white">{content.headline}</p>
          {subline ? <p className="truncate text-xs text-gray-500 dark:text-gray-400">{subline}</p> : null}
        </div>
      </div>

      <a
        href={primaryHref}
        className={`mt-3 block w-full rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors ${styles.primary}`}
      >
        {primaryLabel}
      </a>

      {onNotYou ? (
        <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
          {t('Not you?')}{' '}
          <button
            type="button"
            onClick={onNotYou}
            className="font-medium text-teal-600 hover:text-teal-500 dark:text-teal-400"
          >
            {t('Sign up as someone else')}
          </button>
        </p>
      ) : null}
    </div>
  );
}
